import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import './JobQueue.css'
import 'devextreme/dist/css/dx.common.css'
import 'devextreme/dist/css/dx.light.css'
import { getAllJobs } from '../../redux/slices'
import Footer from '../../components/Footer/Footer'
import ComponentBox from '../../components/ComponentBox/ComponentBox'
import { createEntityAdapter } from '@reduxjs/toolkit'
import ICSDataGrid from '../../components/ICSDataGrid/ICSDataGrid'
import { renderDateTime } from '../../utils/dateFormatter'
import { DefaultState } from '../../types'
import ICSDateBox from '../../components/ICSDateBox/ICSDateBox'
import ICSButtonGroup from '../../components/ICSButtonGroup/ICSButtonGroup'
import Joi from 'joi'
import { useForm } from 'react-hook-form'
import { joiResolver } from '@hookform/resolvers/joi'
import { Container, Row, Col, Form, FormGroup, ModalBody } from 'reactstrap'
import ComponentBoxFooter from '../../components/ComponentBox/ComponentBoxFooter/ComponentBoxFooter'
import ICSButton from '../../components/ICS/Button/ICSButton'
import HeaderBar from '../../components/HeaderBar/HeaderBar'
import { capitalizeFirstLetter } from '../../utils/helpers'
import { Badge } from 'reactstrap'
import { ICSModal } from '../../components'
import ICSErrorCard from '../../components/ICSErrorCard/ICSErrorCard'
import { AppDispatch } from '../../services/lib'

const schema = Joi.object({
  dateType: Joi.array().items(Joi.string()).required().messages({
    'string.empty': `Required field`,
  }),
  start: Joi.date().iso().required(),
  end: Joi.date().iso().allow('').allow(null).optional(),
})

const badgeColorMap = {
  new: 'secondary',
  running: 'primary',
  completed: 'success',
  failed: "danger"
}

const StatusBadge = (data: { value: string }) => {
  return (
    <Badge color={badgeColorMap[data.value] || 'primary'} pill>
      {capitalizeFirstLetter(data.value)}
    </Badge>
  )
}

export const JobExitDetailView = props => {
  const [hidden, setHidden] = useState<boolean>(false)
  const toggle = () => {
    setHidden(hidden ? false : true)
  }

  const extendedDisplay = (
    <>
      <ICSModal
        visible={hidden}
        onHiding={toggle}
        dragEnabled={false}
        closeOnOutsideClick={true}
      >
        <ModalBody>
        <pre>{props.value}</pre>
        </ModalBody>
      </ICSModal>
    </>
  )

  return (
    <>
      {extendedDisplay}

      {props.value.length > 10 ? (
        <ICSButton onClick={toggle} type='default' stylingMode='outlined'>
          Show
        </ICSButton>
      ) : (
        props.value
      )}
    </>
  )
}

const JobQueue = () => {
  const dispatch: AppDispatch = useDispatch()
  useEffect(() => {
    const initialState = async () => {
      await dispatch(getAllJobs({}))
    }
    initialState()
  }, [dispatch])
  const selectors = createEntityAdapter().getSelectors()
  const jobsState = useSelector((state: DefaultState) => state.jobs)
  const jobsError = useSelector((state: DefaultState) => state.jobs.error)
  const jobsArray = selectors.selectAll(jobsState)

  const userData = useSelector((state: DefaultState) => state.session.data.user)

  const isAdmin = userData?.roles?.includes("admin") ? true : false

  const {
    formState: { errors, isValid },
    handleSubmit,
    control,
    getValues,
  } = useForm({
    mode: 'onChange',
    resolver: joiResolver(schema),
  })

  const onSearch = () => {
    const { start, end, dateType } = getValues()

    const queryObj: { [k: string]: string | number } = {
      start: start ? encodeURIComponent(start) : 0,
      end: end ? encodeURIComponent(end) : 0,
      dateType: (dateType[0] || 'Created').toLowerCase(),
    }

    dispatch(getAllJobs(queryObj))
    return
  }

  const columnOptions = [
    {
      dataField: 'id',
      caption: 'Job #',
      width: '90px',
    },
    {
      dataField: 'result_id',
      caption: 'Result Id',
      width: '90px',
    },
    {
      dataField: 'analyzer.name',
      caption: 'Analyzer',
    },
    {
      dataField: 'status',
      caption: 'Status',
      cellRender: StatusBadge,
      width: '90px',
    },
    {
      dataField: 'created_at',
      caption: 'Created At',
      cellRender: renderDateTime,
      width: '160px',
    },
    {
      dataField: 'job.processed_at',
      caption: 'Started',
      cellRender: renderDateTime,
      defaultSortOrder: 'desc',
      width: '200px',
    },
    {
      dataField: 'job.finished_at',
      caption: 'Finished',
      cellRender: renderDateTime,
      defaultSortOrder: 'desc',
      width: '200px',
    },
    {
      dataField: 'user.name',
      caption: 'User',
      width: '200px',
    },
  ]

  return (
    <>
      <Container fluid={true}>
        <Row>
          <Col className='wivi-col'>
            <HeaderBar
              origin='IPA'
              routes={[
                {
                  title: 'Run Queue',
                },
              ]}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={{ size: 12 }} className='wivi-col'>
            <ComponentBox title='Run Queue' isCustom={true} cardClass='card-container' cardBodyId='resultsCardBody'>
              <Form onSubmit={handleSubmit(onSearch)}>
                <FormGroup>
                  <Row>
                    <Col>
                      <ICSDateBox
                        name='start'
                        control={control}
                        errors={errors}
                        label='Start'
                      />
                    </Col>
                    <Col>
                      <ICSDateBox
                        name='end'
                        control={control}
                        errors={errors}
                        label='End'
                      />
                    </Col>
                    <Col>
                      <ICSButtonGroup
                        name='dateType'
                        id='dateType'
                        control={control}
                        errors={errors}
                        label='Search by Date'
                        items={[
                          { text: 'Created', type: 'default' },
                          { text: 'Started', type: 'default' },
                          { text: 'Finished', type: 'default' },
                        ]}
                      />
                    </Col>
                  </Row>
                  <ComponentBoxFooter>
                    <ICSButton
                      id='formSubmit'
                      width={60}
                      useSubmitBehavior={true}
                      type='success'
                      disabled={!isValid}
                    >
                      Search
                    </ICSButton>
                  </ComponentBoxFooter>
                </FormGroup>
              </Form>
            </ComponentBox>
          </Col>
        </Row>
        <Row>
          <Col xs={{ size: 12 }} className='wivi-col'>
            <ComponentBox>
              {jobsError?.code === 'error' ? (
                <ICSErrorCard
                  code={jobsError.code}
                  message={jobsError.message}
                  fields={jobsError.fields}
                />
              ) : (
                <ICSDataGrid
                  dataSource={jobsArray}
                  columnOptions={
                    isAdmin
                      ? [
                          ...columnOptions,
                          {
                            dataField: 'job.returnvalue',
                            caption: 'Exit Payload',
                            cellRender: (data: {
                              value: Record<string, unknown>
                            }) => {
                              return (
                                <JobExitDetailView
                                  value={JSON.stringify(data.value, null, 2)}
                                />
                              )
                            },
                          },
                        ]
                      : columnOptions
                  }
                />
              )}
            </ComponentBox>
          </Col>
        </Row>
        <Footer />
      </Container>
    </>
  )
}

export default JobQueue
